import { CmdInterpreter } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { wordsToCommands } from 'BMapsUtil/speech_utils';
import { getChainForIndex, getIndexForChain } from 'BMapsUtil/js_utils';
import { textToAction } from '../gibbs/text-to-action';

const COMPOUND_LABEL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * @typedef {{ resSpec, cmpdIndex, selectString }} EntityRef
 * cmpdIndex is a string label like "A", "B", ..., "Z", "AA", ...
 */

/**
 * @description Takes a string input, converts it to commands using the commands API,
 * applies the resulting command representations, and publishes human friendly descriptions.
 * @param {string} input
 * @returns {Promise<void>}
 */
export async function applyWordsAsCommands(input) {
    if (!input || input.length === 0) {
        return;
    }

    const context = {};
    const focusCmpd = App.Workspace.getActiveCompound();
    if (focusCmpd) {
        context.focusCompound = { cmpdIndex: getAliasForCompound(focusCmpd) };
    }
    const commandArr = await wordsToCommands(input, context);
    EventBroker.publish('commandInput', { inputWords: input });
    const interpretedCmds = await CmdInterpreter.interpretCommand(commandArr);
    EventBroker.publish('commandInput', { interpretedCmds });
}

/**
 * Process natural language input through the Gibbs text-to-action pipeline.
 * Uses the action registry and LLM to convert input to a validated action plan,
 * then dispatches through CmdInterpreter.interpretActionPlan().
 *
 * @param {string} input - Natural language input from user
 * @returns {Promise<Object>} Execution result from interpretActionPlan
 */
export async function applyTextAsActionPlan(input) {
    if (!input || input.length === 0) {
        return { type: 'error', error: 'Empty input' };
    }

    EventBroker.publish('commandInput', { inputWords: input, mode: 'action-plan' });

    const result = await textToAction(input, App.Workspace);

    if (result.type === 'plan') {
        const execResult = await CmdInterpreter.interpretActionPlan(result.plan);
        EventBroker.publish('commandInput', { interpretedActionPlan: execResult });
        return execResult;
    }

    if (result.type === 'clarification') {
        EventBroker.publish('commandInput', { clarification: result.clarification });
        return { type: 'clarification', clarification: result.clarification };
    }

    if (result.type === 'chat') {
        EventBroker.publish('commandInput', { chatMessage: result.message });
        return { type: 'chat', message: result.message };
    }

    if (result.type === 'invalid') {
        return { type: 'error', errors: result.errors };
    }

    return { type: 'error', error: 'Unexpected result type' };
}

/**
 * Return the letter label for a compound (A, B, C, ..., Z, AA, AB, ...).
 * @param {import('BMapsModel').Compound} compound
 * @returns {string | null}
 */
export function getAliasForCompound(compound) {
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    const zeroIndexed = loadedCompounds.indexOf(compound);
    return zeroIndexed !== -1 ? getChainForIndex(zeroIndexed, COMPOUND_LABEL_CHARS) : null;
}

/**
 * Return the compound for a given label (e.g. "A", "B", "AA").
 * @param {string} label
 * @returns {import('BMapsModel').Compound | null}
 */
export function getCompoundForAlias(label) {
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    const zeroIndexed = getIndexForChain(String(label), COMPOUND_LABEL_CHARS);
    if (zeroIndexed === -1 || zeroIndexed >= loadedCompounds.length) {
        console.warn(`Warning: invalid compound label: ${label}. Must be a label like A, B, ..., Z, AA, ...`);
        return null;
    }
    return loadedCompounds[zeroIndexed] || null;
}

/**
 * Convert an entity reference into displayable version.
 * This returns resSpec and select strings as they are.
 * For cmpdIndex, it returns: Compound <label> (<cmpd name>)
 * @param {EntityRef | EntityRef[]} entityRef
 * @returns {string}
 */
export function formatLabels(entityRef) {
    if (Array.isArray(entityRef)) {
        return entityRef.map(formatLabels).join(', ');
    }
    if (!entityRef || typeof entityRef !== 'object') return '(unknown)';
    const { cmpdIndex, resSpec, selectString } = entityRef;
    if (cmpdIndex != null) {
        const cmpd = getCompoundForAlias(cmpdIndex);
        const name = cmpd?.displayName || 'unknown';
        return `Compound ${cmpdIndex} (${name})`;
    }
    if (selectString) return selectString;
    if (resSpec) return resSpec;
    return '(unknown)';
}
